/* Etiquetas (Label Templates) — Phase 1 + 2 + 3
 * CRUD de templates + Editor Visual drag-and-drop (react-rnd) + Print de etiquetas via PDF (reportlab).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Rnd } from 'react-rnd';
import {
  Tag, Plus, X, Save, Copy, Trash2, Printer, Eye, Layers, MoveDiagonal2, Share2, Check,
  Download, Upload,
} from 'lucide-react';
import { API } from '../lib/api';
import { confirmDialog } from '../components/ConfirmDialog';

const ERP = `${API}/erp`;

const MODULES = [
  { key: 'produto', label: 'Produto' },
  { key: 'venda', label: 'Venda' },
  { key: 'ecommerce', label: 'Ecommerce' },
  { key: 'nfe', label: 'NF-e' },
  { key: 'nfce', label: 'NFC-e' },
  { key: 'coleta', label: 'Ordem de Coleta' },
];
const moduleLabel = (k) => (MODULES.find(m => m.key === k) || {}).label || k;

const FIELD_OPTIONS = [
  { key: 'free_text', label: 'Campo Livre', type: 'free_text' },
  { key: 'code', label: 'Codigo', type: 'text' },
  { key: 'sku', label: 'Codigo SKU', type: 'text' },
  { key: 'barcode', label: 'Cod de Barras', type: 'barcode' },
  { key: 'barcode', label: 'QR Code', type: 'qrcode' },
  { key: 'name', label: 'Nome do Produto', type: 'text' },
  { key: 'unit', label: 'Unidade', type: 'text' },
  { key: 'sale_price', label: 'Valor de Venda', type: 'text' },
  { key: 'promo_price', label: 'Valor Promocional', type: 'text' },
  { key: 'unit_price', label: 'Vl. Unitario', type: 'text' },
  { key: 'weight_net', label: 'Peso Liquido', type: 'text' },
  { key: 'weight_gross', label: 'Peso Bruto', type: 'text' },
  { key: 'ncm', label: 'NCM', type: 'text' },
  { key: 'brand', label: 'Marca', type: 'text' },
  { key: 'category', label: 'Categoria', type: 'text' },
  { key: 'tag_size', label: 'Tamanho', type: 'text' },
  { key: 'price_table', label: 'Tabela de Preco', type: 'text' },
  // iter77dc.6: variaveis do modulo VENDA — populadas a partir da venda que
  // acionou a impressao. Nas demais impressoes, renderizam vazio.
  { key: 'company_name', label: 'Nome da Empresa (Venda)', type: 'text', moduleOnly: 'venda' },
  { key: 'client_name', label: 'Nome do Cliente (Venda)', type: 'text', moduleOnly: 'venda' },
  { key: 'store_qty', label: 'QTD Produto por Loja', type: 'text', moduleOnly: 'venda' },
  { key: 'store_name', label: 'N° da Loja', type: 'text', moduleOnly: 'venda' },
  { key: '_logo', label: 'Logomarca', type: 'logo' },
  { key: '_box', label: 'Caixa/Moldura', type: 'box' },
];

const defaultElement = (field) => ({
  type: field.type,
  field_key: field.key.startsWith('_') ? null : field.key,
  free_text: field.type === 'free_text' ? 'Texto livre' : '',
  x_mm: 4,
  y_mm: 4,
  w_mm: field.type === 'barcode' ? 40 : (field.type === 'qrcode' ? 20 : (field.type === 'logo' ? 25 : 30)),
  h_mm: field.type === 'barcode' ? 12 : (field.type === 'qrcode' ? 20 : (field.type === 'logo' ? 12 : 6)),
  font_size: 10,
  font_weight: 'normal',
  align: 'left',
  prefix: '',
  suffix: '',
  rotation: 0,
  barcode_type: 'code128',
});

const PT_LABEL = {
  text: 'Texto', barcode: 'Cod.Barras', qrcode: 'QR', logo: 'Logo', box: 'Caixa', free_text: 'Livre',
};

// iter77r: WYSIWYG sample data — espelha _resolve_field do backend (labels.py)
const SAMPLE_PRODUCT = {
  code: 'PRD-001',
  sku: 'SKU-001',
  barcode: '7891234567890',
  name: 'Produto de Exemplo para Visualizacao',
  unit: 'UN',
  unit_price: 'R$ 9,99',
  sale_price: 'R$ 99,90',
  promo_price: 'R$ 79,90',
  weight_net: '0,45',
  weight_gross: '0,50',
  ncm: '12345678',
  brand: 'MarcaX',
  category: 'Categoria',
  tag_size: 'M',
  price_table: 'R$ 89,90',
  // iter77dc.7: preview das variaveis do modulo Venda
  company_name: 'NOME DA EMPRESA',
  client_name: 'NOME DO CLIENTE',
  store_name: 'Loja 001',
  store_qty: '15',
};
const PT_PER_MM = 2.83465; // 1mm = 2.83465 pt (ReportLab)

// iter77r: replica _wrap_text_lines/_draw_text do labels.py para WYSIWYG real
let _measureCtx = null;
const _measureText = (text, fontSizePx, bold) => {
  if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d');
  _measureCtx.font = `${bold ? 'bold ' : ''}${fontSizePx}px Helvetica, Arial, sans-serif`;
  return _measureCtx.measureText(text).width;
};
const _wrapLines = (text, maxWidthPx, fontSizePx, bold) => {
  const lines = []; let cur = '';
  for (const word of text.split(' ')) {
    const candidate = (cur + ' ' + word).trim();
    if (_measureText(candidate, fontSizePx, bold) <= maxWidthPx) { cur = candidate; }
    else {
      if (cur) lines.push(cur);
      if (_measureText(word, fontSizePx, bold) > maxWidthPx) {
        let buf = '';
        for (const ch of word) {
          if (_measureText(buf + ch, fontSizePx, bold) <= maxWidthPx) buf += ch;
          else { if (buf) lines.push(buf); buf = ch; }
        }
        cur = buf;
      } else { cur = word; }
    }
  }
  if (cur) lines.push(cur);
  return lines;
};
const autoFitText = (text, maxWPx, maxHPx, requestedFontPt, bold, ptToPxFactor) => {
  let fs = Math.max(4, Number(requestedFontPt) || 10);
  while (fs >= 4) {
    const fsPx = fs * ptToPxFactor;
    const lines = _wrapLines(text, maxWPx, fsPx, bold);
    const lineH = fsPx * 1.15;
    if (lines.length > 0 && lines.length * lineH <= maxHPx) {
      return { fontPx: fsPx, lines };
    }
    fs -= 0.5;
  }
  // fallback truncate
  const fsPx = 4 * ptToPxFactor;
  let s = text;
  while (s && _measureText(s + '...', fsPx, bold) > maxWPx) s = s.slice(0, -1);
  return { fontPx: fsPx, lines: [s + (s !== text ? '...' : '')] };
};

export const LabelsPage = ({ companyId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [showFiliais, setShowFiliais] = useState(false);
  const [editing, setEditing] = useState(null);   // template object being edited (or {} for new)
  const [printing, setPrinting] = useState(null); // {template, products}
  const [sharing, setSharing] = useState(null);   // iter143: template para compartilhar com filiais

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (moduleFilter) params.set('module', moduleFilter);
      if (search) params.set('search', search);
      if (companyId && !showFiliais) params.set('company_id', companyId);
      const { data } = await axios.get(`${ERP}/label-templates?${params}`, { withCredentials: true });
      setItems(data.data || []);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || 'desconhecido';
      toast.error(`Erro ao carregar etiquetas${status ? ` (HTTP ${status})` : ''}: ${String(detail).slice(0, 200)}`);
    } finally { setLoading(false); }
  }, [moduleFilter, search, companyId, showFiliais]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Excluir este template de etiqueta?', confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${ERP}/label-templates/${id}`, { withCredentials: true });
      toast.success('Etiqueta excluida');
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Erro';
      toast.error(`Erro ao excluir: ${String(detail).slice(0, 200)}`);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${ERP}/label-templates/${id}/duplicate`, {}, { withCredentials: true });
      toast.success('Etiqueta duplicada');
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Erro';
      toast.error(`Erro ao duplicar: ${String(detail).slice(0, 200)}`);
    }
  };

  // iter156dt11: Download do modelo — exporta JSON portavel para reuso em outra filial/cliente.
  // Remove metadados especificos (ids, tenant, timestamps, company_ids) para evitar conflito.
  const handleExport = (t) => {
    const clean = {
      __label_template_export__: true,
      __version__: 1,
      description: t.description,
      module: t.module,
      format: t.format,
      orientation: t.orientation,
      width_mm: t.width_mm,
      height_mm: t.height_mm,
      is_default: false, // padrao false na importacao — usuario decide depois
      elements: (t.elements || []).map(e => ({ ...e })), // shallow copy
      active: true,
    };
    const blob = new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = String(t.description || 'modelo').replace(/[^\w\d-]+/g, '_').slice(0, 40);
    a.download = `etiqueta_${t.code || 'sc'}_${safe}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Modelo baixado — importe no cliente/filial destino');
  };

  // iter156dt11: Upload do modelo — importa JSON de outro cliente/filial.
  const handleImportClick = () => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'application/json,.json';
    inp.onchange = async () => {
      const f = inp.files && inp.files[0];
      if (!f) return;
      try {
        const text = await f.text();
        const parsed = JSON.parse(text);
        if (!parsed || !parsed.__label_template_export__) {
          toast.error('Arquivo invalido — nao eh um export de modelo de etiqueta');
          return;
        }
        // Payload de criacao — deixa o backend gerar id, tenant, code, timestamps.
        const payload = {
          description: parsed.description ? `${parsed.description} (importado)` : 'Modelo importado',
          module: parsed.module || 'produto',
          format: parsed.format || 'custom',
          orientation: parsed.orientation || 'paisagem',
          width_mm: parsed.width_mm || 60,
          height_mm: parsed.height_mm || 30,
          is_default: false,
          elements: parsed.elements || [],
          active: true,
          company_ids: companyId ? [companyId] : [],
        };
        await axios.post(`${ERP}/label-templates`, payload, { withCredentials: true });
        toast.success('Modelo importado — revise os detalhes antes de imprimir');
        fetchData();
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'Erro';
        toast.error(`Falha na importacao: ${String(detail).slice(0, 200)}`);
      }
    };
    inp.click();
  };

  return (
    <div data-testid="labels-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center"><Tag size={16} className="text-orange-700" /></div>
          <div>
            <h2 className="text-lg font-black text-slate-900">Etiquetas</h2>
            <p className="text-xs text-slate-500">Modelos personalizados de etiqueta para produtos, vendas e notas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* iter156dt11: ico discreto pra importar modelo JSON exportado de outro cliente/filial */}
          <button
            onClick={handleImportClick}
            title="Importar modelo (JSON) exportado de outro cliente/filial"
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="labels-import-btn"
          >
            <Upload size={14} />
          </button>
          <button onClick={() => setEditing({ description: '', width_mm: 60, height_mm: 30, module: 'produto', is_default: false, format: 'custom', orientation: 'paisagem', elements: [], active: true, company_ids: companyId ? [companyId] : [] })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-700 hover:bg-orange-800 text-white text-xs font-bold shadow-md" data-testid="labels-new-btn">
            <Plus size={14} /><span>Inserir</span>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3 bg-white border border-slate-200 rounded-xl p-3">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cod ou Descricao..." className="flex-1 min-w-[200px] px-3 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="labels-search" />
        <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-white" data-testid="labels-module-filter">
          <option value="">Todos modulos</option>
          {MODULES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={showFiliais} onChange={e => setShowFiliais(e.target.checked)} />
          <span>Mostrar registros de filiais?</span>
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[760px] text-xs" data-testid="labels-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-slate-500">
              <th className="px-3 py-2">Cod</th>
              <th className="px-3 py-2">Descricao</th>
              <th className="px-3 py-2">Padrao (Por Modulo)</th>
              <th className="px-3 py-2">Modulo</th>
              <th className="px-3 py-2 text-right">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Carregando...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Nenhuma etiqueta cadastrada. Clique em "Inserir".</td></tr>}
            {!loading && items.map(t => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-orange-50/30" data-testid={`labels-row-${t.id}`}>
                <td className="px-3 py-1.5 font-mono text-slate-600">{t.code}</td>
                <td className="px-3 py-1.5 text-slate-900 font-semibold">{t.description}</td>
                <td className="px-3 py-1.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${t.is_default ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{t.is_default ? 'Sim' : 'Nao'}</span></td>
                <td className="px-3 py-1.5 text-slate-600">{moduleLabel(t.module)}</td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => setPrinting({ template: t })} title="Imprimir amostra" className="p-1.5 text-slate-500 hover:text-orange-700 hover:bg-orange-50 rounded-lg" data-testid={`labels-print-${t.id}`}><Printer size={14} /></button>
                    <button onClick={() => setEditing(t)} title="Editar" className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`labels-edit-${t.id}`}><Eye size={14} /></button>
                    <button onClick={() => setSharing(t)} title="Compartilhar com filiais" className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg" data-testid={`labels-share-${t.id}`}><Share2 size={14} /></button>
                    <button onClick={() => handleDuplicate(t.id)} title="Duplicar" className="p-1.5 text-slate-500 hover:text-violet-600 hover:bg-violet-50 rounded-lg" data-testid={`labels-dup-${t.id}`}><Copy size={14} /></button>
                    {/* iter156dt11: download do modelo em JSON portavel */}
                    <button onClick={() => handleExport(t)} title="Baixar modelo (JSON) para reusar em outro cliente/filial" className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg" data-testid={`labels-export-${t.id}`}><Download size={14} /></button>
                    <button onClick={() => handleDelete(t.id)} title="Excluir" className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg" data-testid={`labels-del-${t.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-3 py-2 text-[11px] text-slate-400">{items.length} registro(s)</div>
      </div>

      {editing && <LabelTemplateModal initial={editing} companyId={companyId} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchData(); }} />}
      {printing && <LabelPrintModal template={printing.template} initialProducts={printing.products} onClose={() => setPrinting(null)} />}
      {sharing && <LabelShareModal template={sharing} onClose={() => setSharing(null)} onSaved={() => { setSharing(null); fetchData(); }} />}
    </div>
  );
};

/* ===================== Share Modal (iter143) ===================== */
const LabelShareModal = ({ template, onClose, onSaved }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set(template.company_ids || []));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/companies`, { withCredentials: true });
        setCompanies(data.data || []);
      } catch {
        // silencioso — modal ainda renderiza e mostra a lista vazia com hint
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = (cid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };
  const setAll = (val) => {
    setSelectedIds(val ? new Set(companies.map(c => c.id)) : new Set());
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const company_ids = Array.from(selectedIds);
      await axios.post(`${ERP}/label-templates/${template.id}/share`, { company_ids }, { withCredentials: true });
      toast.success(company_ids.length === 0 ? 'Compartilhado com TODAS as filiais' : `Compartilhado com ${company_ids.length} filial(is)`);
      onSaved && onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Erro';
      toast.error(`Erro ao compartilhar: ${String(detail).slice(0, 200)}`);
    } finally { setSaving(false); }
  };

  const allSelected = companies.length > 0 && companies.every(c => selectedIds.has(c.id));

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="label-share-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center"><Share2 size={14} className="text-teal-700" /></div>
            <div>
              <div className="text-sm font-black text-slate-900">Compartilhar Etiqueta</div>
              <div className="text-[11px] text-slate-500 truncate max-w-[280px]">{template.description}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg" data-testid="label-share-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500 leading-relaxed">Selecione as filiais que poderao usar esta etiqueta. <b>Nenhuma marcada = disponivel em TODAS.</b></p>
          <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
            <button onClick={() => setAll(!allSelected)} className="text-[11px] font-bold text-teal-700 hover:text-teal-900" data-testid="label-share-toggle-all">
              {allSelected ? 'Desmarcar todas' : 'Marcar todas'}
            </button>
            <span className="text-[11px] text-slate-400">•</span>
            <span className="text-[11px] text-slate-500">{selectedIds.size} de {companies.length} selecionada(s)</span>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {loading && <div className="text-center py-6 text-slate-400 text-xs">Carregando filiais...</div>}
            {!loading && companies.length === 0 && <div className="text-center py-6 text-slate-400 text-xs">Nenhuma filial cadastrada</div>}
            {!loading && companies.map(co => {
              const on = selectedIds.has(co.id);
              return (
                <label key={co.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${on ? 'bg-teal-50 border border-teal-200' : 'hover:bg-slate-50 border border-transparent'}`} data-testid={`label-share-opt-${co.id}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(co.id)} className="w-3.5 h-3.5 accent-teal-600 cursor-pointer" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-900 truncate">{co.trade_name || co.legal_name || co.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">{co.cnpj || co.document || co.id}</div>
                  </div>
                  {on && <Check size={12} className="text-teal-700" />}
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 bg-slate-50 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white text-xs font-bold" data-testid="label-share-cancel">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="label-share-save">
            <Save size={12} /><span>{saving ? 'Salvando...' : 'Salvar'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};


/* ===================== Template Modal (CRUD without drag-drop) ===================== */
const LabelTemplateModal = ({ initial, companyId, onClose, onSaved }) => {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    description: initial?.description || '',
    width_mm: initial?.width_mm || 60,
    height_mm: initial?.height_mm || 30,
    module: initial?.module || 'produto',
    is_default: initial?.is_default || false,
    format: initial?.format || 'custom',
    orientation: initial?.orientation || 'paisagem',
    elements: initial?.elements || [],
    active: initial?.active ?? true,
    company_ids: initial?.company_ids || (companyId ? [companyId] : []),
    // iter77j: divisoes visuais da etiqueta (1 = simples, 2 = dupla, 3 = tripla)
    divisions: initial?.divisions || 1,
  });
  const [saving, setSaving] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [snap, setSnap] = useState(true); // snap to 1mm grid

  const addElement = (field) => setForm(f => {
    const next = [...(f.elements || []), defaultElement(field)];
    setSelectedIdx(next.length - 1);
    return { ...f, elements: next };
  });
  // iter77j: duplica o elemento na lista — copia com offset pequeno em X para visualizacao
  const duplicateEl = (i) => setForm(f => {
    const src = f.elements[i];
    if (!src) return f;
    const copy = { ...src, x_mm: (Number(src.x_mm) || 0) + 2, y_mm: (Number(src.y_mm) || 0) + 2 };
    const next = [...f.elements, copy];
    setSelectedIdx(next.length - 1);
    return { ...f, elements: next };
  });
  const removeEl = (i) => {
    setForm(f => ({ ...f, elements: f.elements.filter((_, idx) => idx !== i) }));
    setSelectedIdx(prev => (prev === i ? null : prev != null && prev > i ? prev - 1 : prev));
  };
  const updEl = (i, patch) => setForm(f => ({ ...f, elements: f.elements.map((e, idx) => idx === i ? { ...e, ...patch } : e) }));

  // Parse "1,5" (pt-BR) and "1.5" alike
  const toNumber = (v) => {
    if (typeof v === 'number') return v;
    if (v == null || v === '') return 0;
    const s = String(v).replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  };

  const handleSave = async () => {
    if (!form.description.trim()) { toast.error('Descricao obrigatoria'); return; }
    const w = toNumber(form.width_mm); const h = toNumber(form.height_mm);
    if (!(w > 0 && h > 0)) { toast.error('Largura/Altura invalidas'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        width_mm: w,
        height_mm: h,
        elements: (form.elements || []).map(el => ({
          ...el,
          x_mm: toNumber(el.x_mm),
          y_mm: toNumber(el.y_mm),
          w_mm: toNumber(el.w_mm),
          h_mm: toNumber(el.h_mm),
          font_size: toNumber(el.font_size) || 10,
          rotation: parseInt(el.rotation || 0, 10) || 0,
        })),
      };
      const cfg = { withCredentials: true, timeout: 30000 };
      if (isNew) {
        await axios.post(`${ERP}/label-templates`, body, cfg);
        toast.success('Etiqueta criada');
      } else {
        await axios.put(`${ERP}/label-templates/${initial.id}`, body, cfg);
        toast.success('Etiqueta atualizada');
      }
      onSaved();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail
        || (typeof e.response?.data === 'string' ? e.response.data : null)
        || (e.code === 'ECONNABORTED' ? 'Tempo esgotado (30s). O servidor demorou para responder.' : null)
        || (!e.response && e.message === 'Network Error' ? 'Sem resposta do servidor (verifique sua conexao OU se o backend foi reimplantado em producao).' : e.message)
        || 'Erro desconhecido';
      toast.error(`Erro ao salvar${status ? ` (HTTP ${status})` : ''}: ${String(detail).slice(0, 240)}`);
    } finally { setSaving(false); }
  };

  const handlePreview = async () => {
    const w = toNumber(form.width_mm); const h = toNumber(form.height_mm);
    if (!(w > 0 && h > 0)) { toast.error('Largura/Altura invalidas'); return; }
    if (!(form.elements || []).length) { toast.error('Adicione pelo menos 1 elemento'); return; }
    try {
      const body = {
        description: form.description || 'Preview',
        width_mm: w,
        height_mm: h,
        module: form.module,
        format: form.format,
        orientation: form.orientation,
        divisions: parseInt(form.divisions || 1, 10) || 1,
        elements: (form.elements || []).map(el => ({
          ...el,
          x_mm: toNumber(el.x_mm),
          y_mm: toNumber(el.y_mm),
          w_mm: toNumber(el.w_mm),
          h_mm: toNumber(el.h_mm),
          font_size: toNumber(el.font_size) || 10,
          rotation: parseInt(el.rotation || 0, 10) || 0,
        })),
      };
      const r = await axios.post(`${ERP}/labels/preview`, body, {
        withCredentials: true,
        responseType: 'blob',
        timeout: 30000,
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const status = e.response?.status;
      let detail = 'Erro';
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt; }
        } catch { detail = e.message; }
      } else {
        detail = e.response?.data?.detail || e.message || 'Erro';
      }
      toast.error(`Erro ao visualizar${status ? ` (HTTP ${status})` : ''}: ${String(detail).slice(0, 220)}`);
    }
  };

  // iter77t: canvasContainerRef + ResizeObserver para medir largura REAL do container,
  // evitando clipping das sub-zonas (DIVs) por previewScale hardcoded.
  const canvasContainerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 420, h: 340 });
  useEffect(() => {
    const node = canvasContainerRef.current;
    if (!node) return;
    const update = () => {
      // iter77u: usa clientWidth (exclui border) + subtrai padding via getComputedStyle
      // para obter o content-box exato. Evita o residual de 4px de border-2 do container.
      const cs = getComputedStyle(node);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const w = Math.max(80, node.clientWidth - padX - 2);   // -2px margem de seguranca
      const h = Math.max(120, node.clientHeight - padY - 2);
      setContainerSize({ w, h });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Live preview scale: px per mm. Mede o container real (containerSize) para garantir
  // que o canvas + sub-zonas caibam SEM clipping. Sem piso forçado para evitar overflow
  // em etiquetas largas (>150mm); a escala apenas cai naturalmente para caber.
  const previewScale = useMemo(() => {
    const maxW = Math.max(80, containerSize.w);
    const maxH = Math.max(120, containerSize.h);
    const sw = maxW / Math.max(1, form.width_mm);
    const sh = maxH / Math.max(1, form.height_mm);
    return Math.max(1.5, Math.min(sw, sh)); // piso reduzido para 1.5px/mm (ainda legivel)
  }, [form.width_mm, form.height_mm, containerSize.w, containerSize.h]);
  const px = (mm) => mm * previewScale;
  const mm = (pixels) => pixels / previewScale;
  const roundMm = useCallback((v) => snap ? Math.round(v) : Math.round(v * 10) / 10, [snap]);

  const selectedEl = selectedIdx != null ? (form.elements || [])[selectedIdx] : null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="label-template-modal">
      <div className="bg-white w-full max-w-5xl max-h-[94vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Layers size={18} className="text-orange-700" />
            <h2 className="font-black text-base text-slate-900">{isNew ? 'Inserir Etiqueta' : `Visualizar Etiqueta — ${initial?.description}`}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="label-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 mb-1">* Descricao</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descricao..." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="label-form-desc" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">* Largura (mm)</label>
              <input type="number" step="0.01" value={form.width_mm} onChange={e => setForm({ ...form, width_mm: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="label-form-w" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">* Altura (mm)</label>
              <input type="number" step="0.01" value={form.height_mm} onChange={e => setForm({ ...form, height_mm: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="label-form-h" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">* Modulo</label>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="label-form-module">
                {MODULES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">* Formato</label>
              <select value={form.format} onChange={e => setForm({ ...form, format: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="label-form-format">
                <option value="custom">Customizado</option>
                <option value="a4">A4 (multiplas por pagina)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">* Tamanho</label>
              <select value={form.orientation} onChange={e => setForm({ ...form, orientation: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="label-form-orient">
                <option value="paisagem">Paisagem</option>
                <option value="retrato">Retrato</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">* Divisoes</label>
              <select value={form.divisions || 1} onChange={e => setForm({ ...form, divisions: parseInt(e.target.value, 10) || 1 })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="label-form-divisions" title="Divide visualmente a etiqueta em partes iguais (util para multi-up impressao)">
                <option value={1}>1 - Simples</option>
                <option value={2}>2 - Dupla</option>
                <option value={3}>3 - Tripla</option>
              </select>
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} data-testid="label-form-default" />
                <span>Padrao do modulo?</span>
              </label>
            </div>
          </div>

          {/* Snap-to-grid toggle */}
          <div className="bg-gradient-to-r from-violet-50 to-cyan-50 border border-violet-200 text-violet-900 text-xs rounded-lg px-3 py-2 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 font-bold"><MoveDiagonal2 size={13} /> Editor Visual — arraste / redimensione os elementos no canvas</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={snap} onChange={e => setSnap(e.target.checked)} className="w-3.5 h-3.5 accent-violet-600" data-testid="label-snap-toggle" />
              <span className="font-bold">Snap 1mm</span>
            </label>
          </div>

          {/* Fields palette + Visual Canvas + Selected element panel */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Field palette */}
            <div className="lg:col-span-3">
              <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Campos</p>
              <div className="space-y-1.5 max-h-[460px] overflow-y-auto pr-1">
                {FIELD_OPTIONS.filter(f => !f.moduleOnly || f.moduleOnly === form.module).map((f, idx) => (
                  <button key={idx} onClick={() => addElement(f)} className={`w-full text-left px-2.5 py-1.5 border rounded-lg text-xs font-semibold transition-colors ${f.moduleOnly ? 'border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-800' : 'border-slate-300 bg-white hover:bg-orange-50 hover:border-orange-400 text-slate-700'}`} data-testid={`label-field-${f.type}-${f.key}`}>
                    <span>{f.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Visual canvas (react-rnd) */}
            <div className="lg:col-span-6">
              <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2 text-center">Editor Visual ({form.width_mm}mm x {form.height_mm}mm)</p>
              <div ref={canvasContainerRef} className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-xl p-4 flex justify-center items-center overflow-auto" style={{ minHeight: '340px' }}>
                <div
                  className="relative bg-white shadow-sm overflow-hidden"
                  style={{ width: px(form.width_mm), height: px(form.height_mm), backgroundImage: 'linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)', backgroundSize: `${previewScale}px ${previewScale}px`, border: '1px solid #cbd5e1' }}
                  onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedIdx(null); }}
                  data-testid="label-canvas"
                >
                  {(() => {
                    // iter77s: Cada divisao vira um SUB-CANVAS independente do mesmo tamanho,
                    // garantindo "ambientes" iguais para o usuario editar.
                    // Elementos sao agrupados pela posicao absoluta (X) e renderizados DENTRO
                    // da sub-zona correspondente, com bounds locais. O storage em el.x_mm
                    // continua absoluto (compat com backend/labels.py — sem mudanca de schema).
                    const N = Math.max(1, form.divisions || 1);
                    const divW_mm = (form.width_mm || 1) / N;
                    const computeDivIdx = (el) => {
                      const cx = (Number(el.x_mm) || 0) + (Number(el.w_mm) || 0) / 2;
                      return Math.min(N - 1, Math.max(0, Math.floor(cx / divW_mm)));
                    };
                    const ZONE_BG = (di) => di % 2 === 0 ? 'rgba(139, 92, 246, 0.05)' : 'rgba(217, 70, 239, 0.05)';
                    const ZONE_BORDER = '#a855f7';
                    // Pixel-perfect: usa Math.round para garantir larguras iguais (sem sub-pixel)
                    const zoneWidthPx = Math.floor(px(form.width_mm) / N);
                    const zoneRest = px(form.width_mm) - zoneWidthPx * N; // distribui sobra (geralmente 0)
                    let cursor = 0;
                    return Array.from({ length: N }).map((_, di) => {
                      const wPxThis = zoneWidthPx + (di < zoneRest ? 1 : 0);
                      const leftPx = cursor; cursor += wPxThis;
                      return (
                        <div
                          key={`zone-${di}`}
                          className="absolute top-0 bottom-0"
                          style={{
                            left: `${leftPx}px`,
                            width: `${wPxThis}px`,
                            background: ZONE_BG(di),
                            borderRight: di < N - 1 ? `2px dashed ${ZONE_BORDER}` : 'none',
                            boxSizing: 'border-box',
                          }}
                          data-testid={`label-division-zone-${di}`}
                          onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedIdx(null); }}
                        >
                          {N > 1 && (
                            <span className="absolute top-1 left-1 text-[8px] font-black text-violet-500/70 pointer-events-none uppercase tracking-wider" style={{ zIndex: 0 }}>
                              Div {di + 1} ({divW_mm.toFixed(1)}mm)
                            </span>
                          )}
                          {(form.elements || []).map((el, i) => {
                            if (computeDivIdx(el) !== di) return null;
                            const isSel = selectedIdx === i;
                            const colors = {
                              text: { bg: 'rgba(251,146,60,0.15)', border: '#fb923c' },
                              free_text: { bg: 'rgba(168,85,247,0.15)', border: '#a855f7' },
                              barcode: { bg: 'rgba(16,185,129,0.15)', border: '#10b981' },
                              qrcode: { bg: 'rgba(14,165,233,0.15)', border: '#0ea5e9' },
                              logo: { bg: 'rgba(244,63,94,0.15)', border: '#f43f5e' },
                              box: { bg: 'rgba(100,116,139,0.10)', border: '#64748b' },
                            }[el.type] || { bg: 'rgba(251,146,60,0.15)', border: '#fb923c' };
                            const relX_mm = (Number(el.x_mm) || 0) - di * divW_mm;
                            return (
                              <Rnd
                                key={i}
                                size={{ width: px(el.w_mm), height: px(el.h_mm) }}
                                position={{ x: px(relX_mm), y: px(el.y_mm) }}
                                bounds="parent"
                                dragGrid={snap ? [previewScale, previewScale] : [1, 1]}
                                resizeGrid={snap ? [previewScale, previewScale] : [1, 1]}
                                onDragStart={() => setSelectedIdx(i)}
                                onDragStop={(e, d) => updEl(i, { x_mm: roundMm(mm(d.x) + di * divW_mm), y_mm: roundMm(mm(d.y)) })}
                                onResizeStop={(e, dir, ref, delta, pos) => updEl(i, { w_mm: roundMm(mm(ref.offsetWidth)), h_mm: roundMm(mm(ref.offsetHeight)), x_mm: roundMm(mm(pos.x) + di * divW_mm), y_mm: roundMm(mm(pos.y)) })}
                                style={{
                                  background: colors.bg,
                                  border: `${isSel ? '2px' : '1px'} solid ${isSel ? '#7c3aed' : colors.border}`,
                                  boxShadow: isSel ? '0 0 0 3px rgba(124,58,237,0.18)' : 'none',
                                  borderRadius: 3,
                                  cursor: 'move',
                                  zIndex: isSel ? 5 : 1,
                                  transition: 'box-shadow 0.12s',
                                }}
                                onClick={() => setSelectedIdx(i)}
                                data-testid={`label-canvas-el-${i}`}
                              >
                                <div className="w-full h-full overflow-hidden" style={{ pointerEvents: 'none' }}>
                                  {(() => {
                                    // iter77r: WYSIWYG real — replica _draw_element/_draw_text do labels.py
                                    const wPx = px(el.w_mm); const hPx = px(el.h_mm);
                                    const ptToPx = previewScale / PT_PER_MM;
                                    if (el.type === 'logo') {
                                      return (
                                        <div className="w-full h-full flex items-center justify-center" style={{ fontSize: Math.max(7, previewScale * 0.5), color: '#f43f5e', fontWeight: 700 }}>LOGO</div>
                                      );
                                    }
                                    if (el.type === 'box') {
                                      return <div className="w-full h-full" style={{ border: '0.5px solid #475569' }} />;
                                    }
                                    if (el.type === 'qrcode') {
                                      const side = Math.min(wPx, hPx);
                                      return (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <div style={{ width: side, height: side, backgroundImage: 'linear-gradient(45deg, #0ea5e9 25%, transparent 25%, transparent 75%, #0ea5e9 75%), linear-gradient(45deg, #0ea5e9 25%, transparent 25%, transparent 75%, #0ea5e9 75%)', backgroundSize: '4px 4px', backgroundPosition: '0 0, 2px 2px', opacity: 0.85 }} />
                                        </div>
                                      );
                                    }
                                    if (el.type === 'barcode') {
                                      const key = el.field_key || 'barcode';
                                      const val = SAMPLE_PRODUCT[key] || SAMPLE_PRODUCT.barcode;
                                      const capFs = Math.max(4, Number(el.font_size) || 6) * ptToPx;
                                      return (
                                        <div className="w-full h-full flex flex-col items-stretch justify-center">
                                          <div className="flex-1 flex items-stretch gap-[1px] px-[1px]">
                                            {Array.from({ length: 32 }).map((_, k) => (
                                              <div key={k} style={{ flex: 1, background: k % 2 === 0 ? '#0f172a' : 'transparent' }} />
                                            ))}
                                          </div>
                                          <div className="text-center" style={{ fontSize: capFs, lineHeight: 1.1, color: '#0f172a' }}>{val}</div>
                                        </div>
                                      );
                                    }
                                    // text / free_text
                                    const bold = el.font_weight === 'bold';
                                    let text = '';
                                    if (el.type === 'free_text') {
                                      text = (el.prefix || '') + (el.free_text || 'Texto Livre') + (el.suffix || '');
                                    } else {
                                      const key = el.field_key || 'name';
                                      const val = SAMPLE_PRODUCT[key] != null ? SAMPLE_PRODUCT[key] : '';
                                      text = (el.prefix || '') + String(val) + (el.suffix || '');
                                    }
                                    if (!text.trim()) text = ' ';
                                    const fit = autoFitText(text, wPx, hPx, el.font_size || 10, bold, ptToPx);
                                    const align = el.align || 'left';
                                    return (
                                      <div className="w-full h-full flex flex-col justify-center" style={{ color: '#0f172a', fontFamily: 'Helvetica, Arial, sans-serif', fontWeight: bold ? 700 : 400, fontSize: fit.fontPx, lineHeight: 1.15, textAlign: align }}>
                                        {fit.lines.map((ln, k) => (<div key={k}>{ln}</div>))}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </Rnd>
                            );
                          })}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-1">Clique em um elemento para editar &middot; Arraste para mover (dentro da divisao) &middot; Use as alcas para redimensionar &middot; Para mover entre divisoes, edite o X(mm) ao lado</p>
            </div>

            {/* Selected element editor */}
            <div className="lg:col-span-3">
              <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Elemento ({form.elements?.length || 0})</p>
              {!selectedEl ? (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center" data-testid="label-elements-list">
                  <p className="text-xs text-slate-400 italic">
                    {form.elements?.length ? 'Selecione um elemento no canvas para editar.' : 'Clique em um campo a esquerda para adiciona-lo.'}
                  </p>
                  {form.elements?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {form.elements.map((el, i) => (
                        <button key={i} onClick={() => setSelectedIdx(i)} className="w-full text-left px-2 py-1 bg-white border border-slate-200 rounded text-[11px] font-bold text-slate-700 hover:bg-orange-50 hover:border-orange-300" data-testid={`label-el-jump-${i}`}>
                          {i + 1}. {PT_LABEL[el.type]}{el.field_key ? ` · ${el.field_key}` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-violet-50/50 border-2 border-violet-300 rounded-lg p-2.5 text-xs space-y-2" data-testid={`label-element-${selectedIdx}`}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-black text-violet-800 truncate">{PT_LABEL[selectedEl.type]}{selectedEl.field_key ? ` · ${selectedEl.field_key}` : ''}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => duplicateEl(selectedIdx)} className="text-violet-600 hover:bg-violet-100 rounded p-0.5" title="Duplicar elemento" data-testid={`label-el-duplicate-${selectedIdx}`}><Copy size={12} /></button>
                      <button onClick={() => removeEl(selectedIdx)} className="text-rose-600 hover:bg-rose-50 rounded p-0.5" title="Remover" data-testid={`label-el-remove-${selectedIdx}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  {selectedEl.type === 'free_text' && (
                    <input value={selectedEl.free_text || ''} onChange={e => updEl(selectedIdx, { free_text: e.target.value })} placeholder="Texto livre" className="w-full px-2 py-1 border border-slate-300 rounded text-[11px]" data-testid="label-el-free-text" />
                  )}
                  <div className="grid grid-cols-2 gap-1.5">
                    <label className="text-[10px] font-bold text-slate-600">X (mm)
                      <input type="number" step="0.5" value={selectedEl.x_mm} onChange={e => updEl(selectedIdx, { x_mm: parseFloat(e.target.value || 0) })} className="w-full px-1.5 py-1 border border-slate-300 rounded text-[11px] text-right" data-testid="label-el-x" />
                    </label>
                    <label className="text-[10px] font-bold text-slate-600">Y (mm)
                      <input type="number" step="0.5" value={selectedEl.y_mm} onChange={e => updEl(selectedIdx, { y_mm: parseFloat(e.target.value || 0) })} className="w-full px-1.5 py-1 border border-slate-300 rounded text-[11px] text-right" data-testid="label-el-y" />
                    </label>
                    <label className="text-[10px] font-bold text-slate-600">Larg (mm)
                      <input type="number" step="0.5" value={selectedEl.w_mm} onChange={e => updEl(selectedIdx, { w_mm: parseFloat(e.target.value || 0) })} className="w-full px-1.5 py-1 border border-slate-300 rounded text-[11px] text-right" data-testid="label-el-w" />
                    </label>
                    <label className="text-[10px] font-bold text-slate-600">Alt (mm)
                      <input type="number" step="0.5" value={selectedEl.h_mm} onChange={e => updEl(selectedIdx, { h_mm: parseFloat(e.target.value || 0) })} className="w-full px-1.5 py-1 border border-slate-300 rounded text-[11px] text-right" data-testid="label-el-h" />
                    </label>
                  </div>
                  {(selectedEl.type === 'text' || selectedEl.type === 'free_text') && (
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-3 gap-1">
                        <label className="text-[10px] font-bold text-slate-600">Fonte
                          <input type="number" step="0.5" value={selectedEl.font_size} onChange={e => updEl(selectedIdx, { font_size: parseFloat(e.target.value || 8) })} className="w-full px-1.5 py-1 border border-slate-300 rounded text-[11px] text-right" />
                        </label>
                        <label className="text-[10px] font-bold text-slate-600">Peso
                          <select value={selectedEl.font_weight} onChange={e => updEl(selectedIdx, { font_weight: e.target.value })} className="w-full px-1 py-1 border border-slate-300 rounded text-[11px] bg-white">
                            <option value="normal">Normal</option><option value="bold">Negrito</option>
                          </select>
                        </label>
                        <label className="text-[10px] font-bold text-slate-600">Align
                          <select value={selectedEl.align} onChange={e => updEl(selectedIdx, { align: e.target.value })} className="w-full px-1 py-1 border border-slate-300 rounded text-[11px] bg-white">
                            <option value="left">Esq</option><option value="center">Centro</option><option value="right">Dir</option>
                          </select>
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <input type="text" value={selectedEl.prefix || ''} onChange={e => updEl(selectedIdx, { prefix: e.target.value })} placeholder="Prefixo" className="px-1.5 py-1 border border-slate-300 rounded text-[11px]" />
                        <input type="text" value={selectedEl.suffix || ''} onChange={e => updEl(selectedIdx, { suffix: e.target.value })} placeholder="Sufixo" className="px-1.5 py-1 border border-slate-300 rounded text-[11px]" />
                      </div>
                    </div>
                  )}
                  {selectedEl.type === 'barcode' && (
                    <label className="text-[10px] font-bold text-slate-600 block">Tipo
                      <select value={selectedEl.barcode_type} onChange={e => updEl(selectedIdx, { barcode_type: e.target.value })} className="w-full px-1 py-1 border border-slate-300 rounded text-[11px] bg-white">
                        <option value="code128">Code128</option>
                        <option value="ean13">EAN-13</option>
                        <option value="ean8">EAN-8</option>
                        <option value="upc">UPC</option>
                      </select>
                    </label>
                  )}
                  <div className="pt-1 border-t border-violet-100">
                    <p className="text-[10px] font-bold text-slate-500 mb-1">Elementos ({form.elements.length}):</p>
                    <div className="space-y-0.5 max-h-32 overflow-y-auto">
                      {form.elements.map((el, i) => (
                        <button key={i} onClick={() => setSelectedIdx(i)} className={`w-full text-left px-2 py-0.5 rounded text-[10px] font-bold ${selectedIdx === i ? 'bg-violet-200 text-violet-900' : 'bg-white border border-slate-200 text-slate-600 hover:bg-orange-50'}`}>
                          {i + 1}. {PT_LABEL[el.type]}{el.field_key ? ` · ${el.field_key}` : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={handlePreview} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border-2 border-violet-600 text-violet-700 bg-white hover:bg-violet-50 text-sm font-bold transition-colors" data-testid="label-preview"><Eye size={14} /><span>Visualizar</span></button>
          <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-700 hover:bg-orange-800 text-white text-sm font-bold disabled:opacity-50" data-testid="label-save"><Save size={14} /><span>{saving ? 'Salvando...' : 'Salvar'}</span></button>
          <button onClick={onClose} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold" data-testid="label-cancel"><X size={14} /><span>Fechar</span></button>
        </div>
      </div>
    </div>
  );
};


/* ===================== Print Modal ===================== */
export const LabelPrintModal = ({ template, initialProducts = null, saleContext = null, onClose }) => {
  const [products, setProducts] = useState(initialProducts || []);
  const [copies, setCopies] = useState(1);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [tplList, setTplList] = useState([]);
  const [tplId, setTplId] = useState(template?.id || '');

  useEffect(() => {
    // load templates of the same module
    axios.get(`${ERP}/label-templates?module=${template?.module || 'produto'}`, { withCredentials: true })
      .then(r => {
        const list = r.data?.data || [];
        setTplList(list);
        // iter77dc.4: auto-selecionar o template padrao do modulo (is_default=true)
        // se nao houver um id explicito recebido via prop
        if (!template?.id) {
          const def = list.find(t => t.is_default) || list[0];
          if (def) setTplId(def.id || def._id);
        }
      })
      .catch(() => {});
  }, [template?.module, template?.id]);

  useEffect(() => {
    if (!search || search.length < 2) { setSearchResults([]); return; }
    let alive = true;
    axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(search)}&limit=10`, { withCredentials: true })
      .then(r => { if (alive) setSearchResults(r.data?.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [search]);

  const addProduct = (p) => {
    if (products.find(x => x.id === p.id)) return;
    setProducts([...products, p]);
    setSearch(''); setSearchResults([]);
  };

  const handlePrint = async () => {
    if (!tplId) { toast.error('Selecione um template'); return; }
    if (!products.length) { toast.error('Selecione ao menos 1 produto'); return; }
    setPrinting(true);
    try {
      // iter77dc.6: envia contexto da venda (empresa/cliente) + meta por linha
      // (loja + quantidade da loja) para popular as novas variaveis da etiqueta.
      const payload = {
        template_id: tplId,
        product_ids: products.map(p => p.id),
        copies_each: Math.max(1, parseInt(copies || 1, 10)),
      };
      if (saleContext) {
        payload.context_company_name = saleContext.company_name || '';
        payload.context_company_id = saleContext.company_id || '';
        payload.context_client_name = saleContext.client_name || '';
      }
      // meta_per_row: usa dados embutidos em cada produto (_store_name, _store_qty)
      // que a origem (ex: ERPDashboard) coloca la ao montar initialProducts a partir
      // de store_distribution. Envia mesmo quando nao ha dados, backend ignora vazio.
      const anyMeta = products.some(p => p._store_name || p._store_qty);
      if (anyMeta) {
        payload.meta_per_row = products.map(p => ({
          store: p._store_name || '',
          store_qty: p._store_qty === undefined || p._store_qty === null ? '' : String(p._store_qty),
        }));
      }
      const r = await axios.post(`${ERP}/labels/print`, payload, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('PDF gerado');
    } catch (e) {
      const status = e.response?.status;
      let detail = 'Erro desconhecido';
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* ignore */ }
      } else {
        detail = e.response?.data?.detail || e.message || detail;
      }
      toast.error(`Erro ao gerar PDF${status ? ` (HTTP ${status})` : ''}: ${String(detail).slice(0, 200)}`);
    } finally { setPrinting(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="label-print-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><Printer size={18} className="text-orange-700" /><h2 className="font-black text-base text-slate-900">Imprimir Etiqueta</h2></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Template</label>
            <select value={tplId} onChange={e => setTplId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="print-template-select">
              <option value="">Selecione...</option>
              {tplList.map(t => <option key={t.id} value={t.id}>{t.description} ({t.width_mm}x{t.height_mm}mm){t.is_default ? ' · Padrao' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Adicionar Produto</label>
            <div className="relative">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="SKU, nome..." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="print-product-search" />
              {searchResults.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                  {searchResults.map(p => (
                    <button key={p.id} onClick={() => addProduct(p)} className="w-full text-left px-3 py-1.5 hover:bg-orange-50 border-b border-slate-50 last:border-0 text-xs" data-testid={`print-search-opt-${p.id}`}>
                      <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{p.sku} · {p.stock} estoque</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Selecionados ({products.length})</label>
            <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
              {products.length === 0 && <div className="text-xs text-slate-400 text-center py-4">Nenhum produto selecionado</div>}
              {products.map((p, i) => (
                <div key={`${p.id}-${i}`} className="flex items-center justify-between px-3 py-1.5 border-b border-slate-50 last:border-0 text-xs">
                  <span className="font-semibold text-slate-700 truncate flex-1">
                    {p.name}
                    {(p._store_name || p._store_qty) && (
                      <span className="ml-2 text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">
                        {p._store_name}{p._store_qty ? ` · ${p._store_qty} un` : ''}
                      </span>
                    )}
                  </span>
                  <button onClick={() => setProducts(products.filter((_, idx) => idx !== i))} className="text-rose-500 ml-2"><X size={12} /></button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Copias por produto</label>
            <input type="number" min="1" value={copies} onChange={e => setCopies(e.target.value)} className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="print-copies" />
          </div>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={handlePrint} disabled={printing} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-700 hover:bg-orange-800 text-white text-sm font-bold disabled:opacity-50" data-testid="print-confirm-btn"><Printer size={14} /><span>{printing ? 'Gerando...' : 'Gerar PDF'}</span></button>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold"><X size={14} className="inline mr-1" />Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default LabelsPage;
